import { Paper, IconButton, Typography, useTheme } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import {
  CloseIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
} from "@/shared/foundation/icons/mui";

import { FileExportService } from "../FileExportService";

import { ExportJobRow } from "./ExportJobRow";
import styles from "./ExportJobToast.module.css";

interface ExportJobToastProps {
  service: FileExportService;
  isStackedBelow?: boolean;
  onExitClicked: () => void;
}

export const ExportJobToast: React.FC<ExportJobToastProps> = ({
  service,
  isStackedBelow = false,
  onExitClicked,
}) => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = React.useState(false);
  const { isToastVisible, jobs } = React.useSyncExternalStore(
    React.useCallback((l) => service.subscribe(l), [service]),
    () => service.getSnapshot(),
  );

  if (!isToastVisible) {
    return null;
  }

  const inProgressCount = jobs.filter(
    (j) => j.status === "pending" || j.status === "processing",
  ).length;
  const completedCount = jobs.filter((j) => j.status === "completed").length;
  const errorCount = jobs.filter(
    (j) => j.status === "failed" || j.status === "expired",
  ).length;

  let title = "Preparing download\u2026";
  if (inProgressCount === 0 && errorCount > 0) {
    title = "Export error";
  } else if (inProgressCount === 0 && completedCount > 0) {
    title =
      completedCount === 1
        ? "Download ready"
        : `${completedCount} downloads ready`;
  }

  return (
    <Paper
      elevation={2}
      className={classNames(styles.toast, {
        [styles.stackedBelow]: isStackedBelow,
      })}
    >
      <div
        className={classNames(styles.header, {
          [styles.stackedBelow]: isStackedBelow,
        })}
        style={{ backgroundColor: theme.palette.toastHeader.main }}
      >
        <Typography variant="subtitle1" className={styles.title}>
          {title}
        </Typography>
        <div className={styles.headerActions}>
          <IconButton onClick={() => setCollapsed((c) => !c)} sx={{ mr: 0.25 }}>
            {collapsed ? (
              <ExpandMoreIcon fontSize="medium" />
            ) : (
              <ExpandLessIcon fontSize="medium" />
            )}
          </IconButton>
          <IconButton onClick={onExitClicked}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </div>
      </div>

      {!collapsed && (
        <div className={styles.body}>
          {jobs.map((job) => (
            <ExportJobRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </Paper>
  );
};
